import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useCellChangeHandler, useRowSelectionHandler, createCellCssClassRules, GridCellConfig, createGridDropDownCommitAfter } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, getRowStyleFlags, IndexParameterCell, onGridReady } from "src/utils/grid/gridStyleUtils";
import { useSAL2020_R01Store } from "./Store_SAL2020_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultSAL2020_R01DetailDto, SAL2020_R01DetailDto } from "./Dto_SAL2020_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";

import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useDetailTableWithItemSearch } from "src/hooks/detail-table";
import { useCodeSearch } from "src/hooks/useCodeSearch";
import { CommonCodeColumn, getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";
import { useSAL2020_R01Modal } from "./hooks/useSAL2020_R01Modal";
import { useSAL2020_R01CellChange } from "./hooks/SAL2020_R01CellChange";

const COLUMN_STATE_KEY = 'SAL2020_R01-detail-grid-column-state';


interface Props {
	gridRef: React.RefObject<AgGridReact<SAL2020_R01DetailDto> | null>;
	defaultSAL2020_R01DetailRow: SAL2020_R01DetailDto;
}

export const DetailTable_SAL2020_R01 = (props: Props) => {

	const { gridRef, defaultSAL2020_R01DetailRow } = props;

	const { 
			SAL2020_R01DetailList, setSAL2020_R01DetailList, 
			selectedSAL2020_R01DetailList, setSelectedSAL2020_R01DetailList, removeSelectedSAL2020_R01DetailList, addSelectedSAL2020_R01DetailList, initSelectedSAL2020_R01DetailList,
			deletedSAL2020_R01DetailList, setDeletedSAL2020_R01DetailList, addDeletedSAL2020_R01DetailList, removeDeletedSAL2020_R01DetailList,
			insertSAL2020_R01DetailList, updateSAL2020_R01DetailList, removeSAL2020_R01DetailList,
			detailColumnState, setDetailColumnState,
			originalSAL2020_R01DetailList, setOriginalSAL2020_R01DetailList,
			detailScrollTop, setDetailScrollTop,
		} = useSAL2020_R01Store();
	const { setCodeSearchModalProps } = useSAL2020_R01Modal();
	const [clickable, setClickable] = useState(true);

	const { unitList } = useCommonCodeStore();

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: SAL2020_R01DetailList,
		defaultGridData: defaultSAL2020_R01DetailRow,
		selectedRow: selectedSAL2020_R01DetailList,
		originalGridData: originalSAL2020_R01DetailList,
		updateRow: (index: number, data: any) => updateSAL2020_R01DetailList(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['itemNm', 'itemSpec', 'modelNo', 'unit', 'itemAmt'],
		insertedDisabledCells: [''],
		commonDisabledCells: ['itemCd'],
		
		pointerCells: ['salesQty', 'salesPrice', 'dcAmt', 'remark'],
		insertedPointerCells: ['itemCd', 'itemNm', 'modelNo', 'itemSpec', 'unit'],
		commonPointerCells: [''],
		
		alignLeftCells: ['']
	}), []);

	const headerNames: Record<string, string> = {
		rowIndex: "",
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		modelNo: '품번',
		itemQty: '수량',
		unit: '단위',
		itemPrice: '단가',
		dcAmt: '할인액',
		itemAmt: '금액',
	}

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<SAL2020_R01DetailDto>({
            idKey: 'valueIndex',
            selectedList: selectedSAL2020_R01DetailList,
            deletedList: deletedSAL2020_R01DetailList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
        selectedSAL2020_R01DetailList, deletedSAL2020_R01DetailList,
        disabledInsertedColumns, disabledColumns,
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
    ]);

	// === DetailTable 내장 핸들러 로직 ===
	const { searchExactItemCode } = useCodeSearch({});

	/**
	 * 편집 가능한 컬럼 목록 정의
	 * 사용자가 직접 입력/수정할 수 있는 필드들을 지정
	 */
	const defaultEditableColumns = ['itemCd', 'salesQty', 'salesPrice', 'dcAmt'];

	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {
		InsertRowHandler({
			event: e,
			dataList: SAL2020_R01DetailList,
			rowData: SAL2020_R01DetailList[rowIndex],
			insertRow: insertSAL2020_R01DetailList,
			defaultRowData: defaultSAL2020_R01DetailRow,
			setClickable
		});
	}, [SAL2020_R01DetailList, insertSAL2020_R01DetailList, defaultSAL2020_R01DetailRow, setClickable]);

	const handleGridDropDownCommitAfter = useMemo(() => 
		createGridDropDownCommitAfter<SAL2020_R01DetailDto>(defaultEditableColumns),
	[defaultEditableColumns],);

	const baseColumnDefs: ColDef<SAL2020_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: SAL2020_R01DetailDto) => renderIndexCell(
				params, 
				(innerParams: SAL2020_R01DetailDto) => getRowStyleFlags (
					innerParams,
					{
						deletedList: deletedSAL2020_R01DetailList,
						selectedList: selectedSAL2020_R01DetailList,
						dataList: SAL2020_R01DetailList
					}),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { insertRowHandler(rowIndex, e); }
					} else { 
						insertRowHandler(rowIndex, e);
					}
				},
				(rowData) => RemoveRowHandler( 
					gridRef,
					SAL2020_R01DetailList, 
					rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
					setClickable, 
					removeSAL2020_R01DetailList, 
					(valueIndex) => addDeletedSAL2020_R01DetailList([valueIndex.toString()])
				),
			)
		),
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 77,
			editable: ((params) => params.data?.inserted === true),
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 161,
			editable: false,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 127,
			editable: false,
		},
		{
			headerName: '품번', colId: 'modelNo', field: 'modelNo', width: 130,
			editable: false,
		},
		{
			headerName: '수량', colId: 'salesQty', field: 'salesQty', width: 60,
			editable: (params) => SAL2020_R01DetailList[params.node.rowIndex!] !== undefined,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '단위', colId: 'unit', field: 'unit', width: 60,
			...CommonCodeColumn.options(unitList, false, CommonCodeColumn.change(updateSAL2020_R01DetailList), handleGridDropDownCommitAfter),
		},
		{
			headerName: '단가', colId: 'salesPrice', field: 'salesPrice', width: 65,
			editable: (params) => SAL2020_R01DetailList[params.node.rowIndex!] !== undefined,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '할인액', colId: 'dcAmt', field: 'dcAmt', width: 65,
			editable: (params) => SAL2020_R01DetailList[params.node.rowIndex!] !== undefined,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '금액', colId: 'itemAmt', field: 'itemAmt', width: 96,
			editable: false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		// ...IndexParameterCell(),
	], [
		deletedSAL2020_R01DetailList, SAL2020_R01DetailList,
	]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<SAL2020_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<SAL2020_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, detailColumnState, headerNames]);
	

	const handleRowClick = useRowSelectionHandler({
		dataList: SAL2020_R01DetailList,
		idKey: 'valueIndex',
		selectedList: selectedSAL2020_R01DetailList,
		deletedList: deletedSAL2020_R01DetailList,
		addSelected: addSelectedSAL2020_R01DetailList,
		removeSelected: removeSelectedSAL2020_R01DetailList,
		clickable, setClickable
	});

	const detailTableHandlers = useSAL2020_R01CellChange({
		SAL2020_R01DetailList,
		originalSAL2020_R01DetailList,
		updateSAL2020_R01DetailList,
		gridRef,
		setCodeSearchModalProps,
	});
	
	/**
	 * 품목 검색 기능이 포함된 상세 테이블 이벤트 핸들러 훅
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 * @param itemSearchConfig - 품목 검색 설정
	 * @param constraints - 제약 조건 (notTransport 등)
	 * @param keyDownConfig - 키다운 네비게이션 설정
	 */
	const cellEventHandlers = useDetailTableWithItemSearch<SAL2020_R01DetailDto>({
		detailList: SAL2020_R01DetailList,
		editableColumns: defaultEditableColumns,
		itemSearchConfig: {
			config: {
				category: '품목', // 검색 카테고리
				responseMapping: {
					// 검색 결과 → 테이블 필드 매핑
					itemCd: 'itemCd',		// 품목코드
					itemNm: 'itemNm',		// 품명
					itemSpec: 'itemSpec',	// 규격
					modelNo: 'modelNo',		// 품번
					itemUnit: 'unit',		// 단위
					itemCost: 'salesPrice'	// 단가
				},
				clearFields: {
					// 품목 검색 시 초기화할 필드들
					itemCd: "", itemNm: "", itemSpec: "", modelNo: "",
					unit: "", salesPrice: 0, dcAmt: 0, itemAmt: 0
				},
				searchFunction: searchExactItemCode // 품목 검색 함수
			},
			settingCodeSearchModalProps: detailTableHandlers.settingCodeSearchModalProps, // 모달 설정 함수
		},
		constraints: {
			notEdit: (params) => {
				if(params.data?.inserted === true) return true;
				return false;
			}
		},
		keyDownConfig: {
			qtyField: 'salesQty',        // 엔터 키 시 수량 필드로 이동
			enableArrowNavigation: true  // 방향키 네비게이션 활성화
		}
	});

	const isRowDraggingRef = useRef(false);

	const handlePostSort = useCallback((params: PostSortRowsParams<SAL2020_R01DetailDto>) => {
		// 드래그 중에는 postSortRows에 의한 강제 정렬을 막아 드래그가 눈에 보이도록 함
		if (isRowDraggingRef.current) return;
		postSortHandler(params, SAL2020_R01DetailList, originalSAL2020_R01DetailList, setSAL2020_R01DetailList);
	}, [SAL2020_R01DetailList, originalSAL2020_R01DetailList, ]);


	const pinnedBottomRowData = useMemo(() => {

		const totalQty : number = SAL2020_R01DetailList
			.reduce((sum, item) => sum + (deletedSAL2020_R01DetailList.includes(item.itemCd) ? 0 : Number(item.salesQty) || 0), 0);
		const totalAmt : number = SAL2020_R01DetailList
			.reduce((sum, item) => sum + (deletedSAL2020_R01DetailList.includes(item.itemCd) ? 0 : Number(item.itemAmt) || 0), 0);

		return [{
			itemCd: SAL2020_R01DetailList.length.toLocaleString() + "건",
			itemQty: totalQty,
			itemAmt: totalAmt
		}];	  
	}, [SAL2020_R01DetailList, deletedSAL2020_R01DetailList,]);

	return (
		<GridContextWrapper<SAL2020_R01DetailDto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setSAL2020_R01DetailList}
			initSelected={initSelectedSAL2020_R01DetailList}
			defaultRowData={defaultSAL2020_R01DetailRow}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}	
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<SAL2020_R01DetailDto>
				ref={gridRef}
				rowData={paddedGridData}
				getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}

				columnStateKey={COLUMN_STATE_KEY} // 자동 컬럼 상태 관리 (localStorage)
				setColumnState={setDetailColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}

				// 행 스타일 규칙 (선택/삭제/수정 상태에 따른 색상)
				rowClassRules={createRowClassRules<SAL2020_R01DetailDto>({
					idKey: 'valueIndex',
					selectedList: selectedSAL2020_R01DetailList,
					deletedList: deletedSAL2020_R01DetailList
				})}

				// 이벤트 핸들러들
				onRowClicked={handleRowClick}                                    // 행 클릭 (선택)
				onCellClicked={cellEventHandlers.handleCellClick}               // 셀 클릭 (편집 시작)
                onCellDoubleClicked={cellEventHandlers.handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}             // 셀 키다운 (네비게이션)
                onCellValueChanged={detailTableHandlers.handleCellChange}
				onCellEditingStopped={(e: any) => {
                    // 원래 핸들러 호출
                    detailTableHandlers.handleCellEditingStopped(e);
                    // 수량/단가/할인 컬럼이면 값 변경이 없어도 강제 재계산
                    const f = e?.colDef?.field as string | undefined;
                    if (!f) return;
                    if (f === 'salesQty' || f === 'salesPrice' || f === 'dcAmt') {
                        const row = e.data as SAL2020_R01DetailDto;
                        if (!row) return;
                        const latestRow = useSAL2020_R01Store.getState().SAL2020_R01DetailList.find(item => item.valueIndex === row.valueIndex) || row;
                        const recalculated = detailTableHandlers.calculateAmount(latestRow);
                        recalculated.isModified = latestRow.isModified;
                        
                        updateSAL2020_R01DetailList(row.valueIndex, recalculated);
                    }
                }}
				pinnedBottomRowData={pinnedBottomRowData}
                rowDragManaged={true}
                rowDragEntireRow={true}
				onRowDragEnter={() => { isRowDraggingRef.current = true; }}
                onRowDragEnd={(e: any) => {
                    const api = e.api;
                    // 정렬 해제하여 사용자가 정한 순서를 유지
                    if (api.applyColumnState) {
                        api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
                    }
                    const reordered: SAL2020_R01DetailDto[] = [];
                    api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
                        if (!node.data) return;
                        reordered.push({
                            ...node.data,
                            orderIndex: idx + 1,
                            orderIndexSeq: 0,
                        });
                    });
                    setSAL2020_R01DetailList(reordered);
					isRowDraggingRef.current = false;
                }}
				

				// 정렬 후 처리 (드래그 중에는 자동 정렬 방지)
				onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가
			/>
		</GridContextWrapper>
	)
}
